import { Badge } from "@/components/ui/badge"

interface ModelProvider {
  name: string
  description: string
  models: string[]
  color: string
  icon: React.ReactNode
}

const providers: ModelProvider[] = [
  {
    name: "OpenAI",
    description: "Industry-leading language models",
    models: ["GPT-4o", "GPT-4 Turbo", "o3", "o1"],
    color: "bg-emerald-500",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
      </svg>
    ),
  },
  {
    name: "Anthropic",
    description: "Safe, capable AI systems",
    models: ["Claude Opus 4", "Claude Sonnet 4", "Claude 3.5"],
    color: "bg-orange-500",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M17.304 3.541h-3.672l6.696 16.918h3.672l-6.696-16.918zm-10.608 0l-6.696 16.918h3.78l1.344-3.528h6.912l1.344 3.528h3.78l-6.696-16.918h-3.768zm-.468 10.404l2.352-6.168 2.352 6.168h-4.704z" />
      </svg>
    ),
  },
  {
    name: "DeepSeek",
    description: "Advanced reasoning models",
    models: ["DeepSeek R1", "DeepSeek V3", "DeepSeek Coder"],
    color: "bg-blue-500",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
      </svg>
    ),
  },
  {
    name: "OpenRouter",
    description: "Access any model via unified API",
    models: ["Llama 3.1", "Mixtral", "Gemini Pro"],
    color: "bg-purple-500",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
  },
]

/**
 * Models section displaying supported AI model providers.
 * Shows provider cards with their available models.
 */
export function ModelsSection() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <Badge className="mb-4 border-primary/30 bg-primary/10 text-primary">
            AI Models
          </Badge>
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Powered by Leading AI Models
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Choose from the best AI models available. Switch providers anytime
            without changing your agent configuration.
          </p>
        </div>

        {/* Provider Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {providers.map((provider) => (
            <div
              key={provider.name}
              className="group rounded-xl border border-neutral-200 bg-white p-6 transition-all hover:border-neutral-300 hover:shadow-lg"
            >
              {/* Provider Icon */}
              <div
                className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg text-white ${provider.color}`}
              >
                {provider.icon}
              </div>

              {/* Provider Info */}
              <h3 className="mb-1 text-lg font-semibold text-neutral-900">
                {provider.name}
              </h3>
              <p className="mb-4 text-sm text-neutral-500">
                {provider.description}
              </p>

              {/* Models List */}
              <div className="flex flex-wrap gap-1.5">
                {provider.models.map((model) => (
                  <span
                    key={model}
                    className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-600"
                  >
                    {model}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
